/**
 * 商店主畫面共用版型（Square / Group 一致）
 * 標題＝商店名稱、活動交付、商家資訊、建立活動、使用說明
 */

import type { ReactNode } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SiDiscord, SiInstagram } from '@icons-pack/react-simple-icons';

/** Lite 社群連結（Discord / IG） */
export interface LiteSocialLinks {
  discord?: string | null;
  ig?: string | null;
}

export interface StoreDashboardViewProps {
  /** 商店名稱（主標題） */
  storeName: string;
  /** 商店類型（選填） */
  storeType: string | null;
  /** 社群連結（Discord、IG），顯示於商家名稱旁 icon，選填 */
  socialLinks?: LiteSocialLinks | null;
  /** 活動交付區塊內容 */
  activityDeliveryContent: ReactNode;
  /** 商家資訊卡右上角按鈕（訂閱、前往設定），選填 */
  merchantExtraButtons?: ReactNode;
  /** 商家資訊卡內「目前方案」區塊，選填 */
  merchantSubscriptionBlock?: ReactNode;
  /** 商家資訊卡內狀態訊息（如：缺少 groupId、載入中、尚未設定）；有值時取代預設的商店名稱/類型列 */
  merchantStatusMessage?: ReactNode;
  /** 預繳名單區塊（顯示於商家資訊下方、使用說明上方），選填 */
  merchantPrepaidBlock?: ReactNode;
  /** 建立活動按鈕點擊 */
  onCreateActivity: () => void;
}

/**
 * 共用商店主畫面：同一套版型給 Group（/lite）與 Square（/lite/shop/:suffix）
 */
export function StoreDashboardView({
  storeName,
  storeType,
  socialLinks,
  activityDeliveryContent,
  merchantExtraButtons,
  merchantSubscriptionBlock,
  merchantStatusMessage,
  merchantPrepaidBlock,
  onCreateActivity,
}: StoreDashboardViewProps) {
  return (
    <div className="min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 px-4 py-4 sm:py-8">
      <div className="max-w-2xl mx-auto">
        {/* 標題：商店名稱置中；Discord / IG icon 固定在最右側 */}
        <div className="mb-6 sm:mb-8">
          <div className="relative flex items-center justify-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-0 text-center">
              {storeName || '（未設定名稱）'}
            </h1>
            {(socialLinks?.discord || socialLinks?.ig) && (
              <div className="absolute right-0 flex items-center gap-1.5">
                {socialLinks.discord && (
                  <a
                    href={socialLinks.discord}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Discord 討論區"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full text-indigo-800 hover:text-indigo-900 transition"
                  >
                    <SiDiscord className="h-5 w-5" aria-hidden />
                  </a>
                )}
                {socialLinks.ig && (
                  <a
                    href={socialLinks.ig}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Instagram"
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full text-pink-700 hover:text-pink-800 transition"
                  >
                    <SiInstagram className="h-5 w-5" aria-hidden />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 活動交付 */}
        <Card className="shadow-sm">
          <CardHeader className="text-center">
            <CardTitle>活動交付</CardTitle>
          </CardHeader>
          <CardContent>{activityDeliveryContent}</CardContent>
        </Card>

        {/* 商家資訊 */}
        <Card className="mt-6 sm:mt-8 shadow-sm">
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle>商家資訊</CardTitle>
            </div>
            {merchantExtraButtons && (
              <div className="flex items-center gap-2 shrink-0">
                {merchantExtraButtons}
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {merchantStatusMessage ?? (
              <>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">商店名稱</span>
                  <span className="font-semibold text-gray-900 truncate">
                    {storeName || '（未設定）'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">商家類型</span>
                  <span className="font-semibold text-gray-900 truncate">
                    {storeType || '（未設定）'}
                  </span>
                </div>
              </>
            )}
            {merchantSubscriptionBlock}
          </CardContent>
          <CardFooter>
            <Button className="w-full" size="lg" onClick={onCreateActivity}>
              建立活動
            </Button>
          </CardFooter>
        </Card>

        {/* 預繳名單（選填） */}
        {merchantPrepaidBlock != null && (
          <Card className="mt-6 sm:mt-8 shadow-sm">
            <CardHeader>
              <CardTitle>預繳名單</CardTitle>
            </CardHeader>
            <CardContent>{merchantPrepaidBlock}</CardContent>
          </Card>
        )}

        {/* 使用說明 */}
        <Card className="mt-6 sm:mt-8 shadow-sm">
          <CardHeader>
            <CardTitle>使用說明</CardTitle>
            <CardDescription>快速了解主要流程</CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-xs sm:text-sm text-muted-foreground">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>
                  活動發佈：建立購買清單、新增商品項目，發佈至 LINE
                  群組並產生分享連結
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-600 mr-2">•</span>
                <span>活動結果統計：查看參與活動的統計與結算結果</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-2">•</span>
                <span>活動交付清單：查看參與名單，管理交付與棄單狀態</span>
              </li>
              <li className="flex items-start">
                <span className="text-amber-600 mr-2">•</span>
                <span>
                  客製活動圖文訊息：在設定中自訂發佈至 LINE 的 Flex
                  圖文標題、內文與按鈕
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-gray-600 mr-2">•</span>
                <span>所有清單資料會儲存在您的租戶中，方便管理</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
